import React, { useEffect, useState } from "react";
import { api, euro } from "../lib/api";
import {
  CalendarCheck, Wallet, Clock, TrendingUp, AlertCircle, Sparkles, Heart,
  Users, FileText, Camera, Plus, ArrowRight, CalendarDays, ChevronRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const WEEKDAYS = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

function formatTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}
function formatRelativeDay(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const dd = new Date(d); dd.setHours(0, 0, 0, 0);
  if (dd.getTime() === today.getTime()) return "Aujourd'hui";
  if (dd.getTime() === tomorrow.getTime()) return "Demain";
  return `${WEEKDAYS[dd.getDay()]} ${dd.getDate()}`;
}
function formatToday() {
  const d = new Date();
  return `${WEEKDAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

function SessionRow({ s }) {
  const paid = s.payment_status === "paid";
  const isPast = new Date(s.starts_at) < new Date();
  return (
    <Link
      to="/agenda"
      className="flex items-center gap-3 sm:gap-4 px-4 py-3 rounded-xl hover:bg-sage-light/60 transition-colors group"
      data-testid={`today-session-${s.id}`}
    >
      <div className="text-center w-14 flex-shrink-0">
        <div className="font-display text-base text-sage tabular-nums">{formatTime(s.starts_at)}</div>
        <div className="text-[10px] text-muted-soft uppercase tracking-wider">{s.duration_min}min</div>
      </div>
      <div className="w-px h-9 bg-border"></div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm truncate">{s.client_name}</div>
        <div className="text-xs text-muted-soft truncate">
          {s.session_type || "Séance"}
          {paid && <span className="ml-2 text-sage">· payée</span>}
          {isPast && !paid && <span className="ml-2 text-[#B58B5C]">· à encaisser</span>}
        </div>
      </div>
      <div className="text-sm font-medium tabular-nums whitespace-nowrap">{euro(s.price)}</div>
      <ChevronRight className="w-4 h-4 text-muted-soft/40 group-hover:text-sage transition-colors" />
    </Link>
  );
}

function QuickTile({ icon: Icon, label, to, accent }) {
  return (
    <Link to={to} className="card-soft card-soft-hover p-5 group flex flex-col items-start" data-testid={`quick-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: accent || "var(--sage-light)" }}>
        <Icon className="w-5 h-5 text-sage" strokeWidth={1.6} />
      </div>
      <div className="font-medium text-sm">{label}</div>
      <div className="text-xs text-sage mt-2 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        Y aller <ArrowRight className="w-3 h-3" />
      </div>
    </Link>
  );
}

function StatLite({ label, value, sub }) {
  return (
    <div className="px-5 py-4 bg-white/60 rounded-xl border border-border/40" data-testid={`stat-${label}`}>
      <div className="text-[10px] uppercase tracking-wider text-muted-soft mb-1">{label}</div>
      <div className="font-display text-lg sm:text-xl tabular-nums">{value}</div>
      {sub && <div className="text-[10px] text-muted-soft mt-0.5">{sub}</div>}
    </div>
  );
}

function AlertCard({ a }) {
  const colors = {
    info: { bg: "var(--sage-light)", text: "#3F4D40", icon: Heart },
    warning: { bg: "#F5E5DC", text: "#8B4D3D", icon: AlertCircle },
    success: { bg: "#EFF3EE", text: "#3F4D40", icon: Sparkles },
  };
  const cfg = colors[a.type] || colors.info;
  const Icon = cfg.icon;
  return (
    <div className="rounded-xl px-3.5 py-2.5 flex items-start gap-2.5" style={{ backgroundColor: cfg.bg, color: cfg.text }} data-testid="alert-item">
      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.6} />
      <span className="text-xs leading-relaxed">{a.message}</span>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [relaunchCount, setRelaunchCount] = useState(null);
  const [relaunching, setRelaunching] = useState(false);
  const isAdmin = user?.email === "capsulepatrimoine@gmail.com";

  useEffect(() => {
    const load = () => {
      api.get("/dashboard/summary")
        .then(({ data }) => setData(data))
        .catch((err) => {
          console.error("Failed to load dashboard summary", err);
          setError("Impossible de charger les données.");
        });
    };
    load();
    if (isAdmin) {
      api.get("/admin/leads/relaunch-count")
        .then(({ data }) => setRelaunchCount(data.count))
        .catch(() => setRelaunchCount(null));
    }
    const onFocus = () => load();
    const onVisible = () => { if (!document.hidden) load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [isAdmin]);

  const triggerRelaunch = async () => {
    if (!window.confirm(`Envoyer 2 emails de relance à ${relaunchCount} prospects dormants ?\nEmail 1 (immédiat) : excuse + rappel bilan\nEmail 2 (J+4) : témoignage douce`)) return;
    setRelaunching(true);
    try {
      const { data } = await api.post("/admin/leads/relaunch");
      alert(`✅ ${data.queued} relances programmées. 1ers emails partent dans les 30 min.`);
      setRelaunchCount(0);
    } catch (e) {
      alert("Erreur : " + (e.response?.data?.detail || e.message));
    }
    setRelaunching(false);
  };

  if (error) return <div className="text-clay">{error}</div>;
  if (!data) return <div className="text-muted-soft">Chargement…</div>;

  const firstName = (user?.full_name || "").split(" ")[0] || "";
  const todaySessions = data.today_sessions || [];
  const weekSessions = data.week_sessions || [];
  const upcomingWeek = weekSessions.filter((s) => !todaySessions.find((t) => t.id === s.id)).slice(0, 5);

  return (
    <div className="space-y-7" data-testid="dashboard-container">
      {/* Admin only — Rattrapage 752 leads */}
      {isAdmin && relaunchCount !== null && relaunchCount > 0 && (
        <div className="card-soft p-5 flex items-center justify-between gap-4 border-l-4" style={{borderLeftColor: "var(--sage)"}} data-testid="admin-relaunch-card">
          <div>
            <div className="text-xs uppercase tracking-wider text-sage font-semibold mb-1">Espace admin</div>
            <div className="font-display text-lg">🌱 {relaunchCount} prospects dormants à relancer</div>
            <div className="text-xs text-muted-soft mt-1">Séquence 2 emails (immédiat + J+4), ton doux, opt-out inclus.</div>
          </div>
          <button onClick={triggerRelaunch} disabled={relaunching}
            className="btn-primary text-sm" data-testid="admin-relaunch-button">
            {relaunching ? "Envoi…" : `Relancer les ${relaunchCount} prospects`}
          </button>
        </div>
      )}
      {/* ── Header ──────────────────────────── */}
      <div>
        <div className="text-xs text-sage uppercase tracking-wider mb-1">{formatToday()}</div>
        <h1 className="font-display text-3xl sm:text-4xl">Bonjour {firstName} 🌿</h1>
        <p className="text-muted-soft mt-1.5 text-sm sm:text-base">
          {todaySessions.length === 0
            ? "Aucune séance prévue aujourd'hui. Belle journée !"
            : `Vous avez ${todaySessions.length} séance${todaySessions.length > 1 ? "s" : ""} aujourd'hui.`}
        </p>
      </div>

      {/* ════════════ PRIORITÉ 1 : AGENDA DU JOUR ════════════ */}
      <div className="card-soft p-6 sm:p-7" data-testid="today-card">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-sage flex items-center justify-center">
              <CalendarDays className="w-5 h-5 text-white" strokeWidth={1.6} />
            </div>
            <div>
              <div className="font-display text-lg">Votre journée</div>
              <div className="text-xs text-muted-soft">Vos séances d'aujourd'hui</div>
            </div>
          </div>
          <Link to="/agenda" className="text-xs text-sage hover:underline inline-flex items-center gap-1" data-testid="today-view-agenda">
            Voir l'agenda complet <ArrowRight className="w-3 h-3"/>
          </Link>
        </div>

        {todaySessions.length === 0 ? (
          <div className="py-8 text-center">
            <div className="w-14 h-14 rounded-2xl bg-sage-light flex items-center justify-center mx-auto mb-3">
              <Heart className="w-6 h-6 text-sage" strokeWidth={1.4} />
            </div>
            <div className="text-sm text-muted-soft mb-4 max-w-sm mx-auto">
              Aucune séance prévue aujourd'hui. Profitez de cette journée pour prendre soin de vous.
            </div>
            <Link to="/agenda" className="btn-sage text-sm inline-flex items-center gap-1.5" data-testid="today-empty-cta">
              <Plus className="w-4 h-4"/> Planifier une séance
            </Link>
          </div>
        ) : (
          <div className="space-y-1">
            {todaySessions.map((s) => <SessionRow key={s.id} s={s} />)}
          </div>
        )}
      </div>

      {/* ════════════ PRIORITÉ 2 : ACTIONS QUOTIDIENNES ════════════ */}
      <div>
        <div className="text-xs uppercase tracking-wider text-muted-soft mb-3">Actions rapides</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <QuickTile icon={Plus} label="Nouvelle séance" to="/agenda" accent="var(--sage-light)" />
          <QuickTile icon={Users} label="Mes clients" to="/clients" />
          <QuickTile icon={FileText} label="Émettre facture" to="/facturation" />
          <QuickTile icon={Camera} label="Scanner un reçu" to="/depenses" />
        </div>
      </div>

      {/* ════════════ PRIORITÉ 3 : CETTE SEMAINE (à venir) ════════════ */}
      {upcomingWeek.length > 0 && (
        <div className="card-soft p-6 sm:p-7" data-testid="week-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-sage-light flex items-center justify-center">
                <CalendarCheck className="w-4 h-4 text-sage" strokeWidth={1.6} />
              </div>
              <div>
                <div className="font-display text-base">Cette semaine</div>
                <div className="text-[11px] text-muted-soft">Prochains rendez-vous</div>
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            {upcomingWeek.map((s) => (
              <Link key={s.id} to="/agenda" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-sage-light/60 transition-colors text-sm">
                <div className="w-20 text-xs text-sage tabular-nums">{formatRelativeDay(s.starts_at)}</div>
                <div className="text-xs text-muted-soft tabular-nums">{formatTime(s.starts_at)}</div>
                <div className="flex-1 truncate font-medium text-xs">{s.client_name}</div>
                <div className="text-xs text-muted-soft tabular-nums whitespace-nowrap">{euro(s.price)}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* ════════════ ALERTES (compact) ════════════ */}
      {data.alerts.length > 0 && (
        <div className="space-y-2">
          {data.alerts.map((a, i) => <AlertCard key={`${a.type}-${a.message}-${i}`} a={a} />)}
        </div>
      )}

      {/* ════════════ DERNIÈRE PRIORITÉ : CHIFFRES DU MOIS (background) ════════════ */}
      <div className="card-soft p-6 sm:p-7" data-testid="kpi-card">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-soft mb-1">Vue d'ensemble · {data.month}</div>
            <div className="font-display text-lg">Comment se porte votre cabinet</div>
          </div>
          <Link to="/finances" className="text-xs text-sage hover:underline inline-flex items-center gap-1">
            Détails <ArrowRight className="w-3 h-3"/>
          </Link>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <StatLite label="Séances réalisées" value={data.sessions_done} sub="ce mois-ci" />
          <StatLite label="Encaissé" value={euro(data.revenue_paid)} sub="paiements reçus" />
          <StatLite label="En attente" value={euro(data.revenue_pending)} sub={`${data.sessions_pending_payment} séance(s)`} />
          <StatLite label="Net après URSSAF" value={euro(data.after_charges)} sub="estimation" />
        </div>

        <div className="pt-5 border-t" style={{ borderColor: "var(--border)" }}>
          <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
            <div>
              <div className="text-xs text-muted-soft">URSSAF estimé · {data.quarter_label}</div>
              <div className="font-display text-2xl mt-0.5">{euro(data.urssaf_estimate_quarter)}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] text-muted-soft">Seuil TVA (37 500 €/an)</div>
              <div className="text-sm font-medium">{data.tva_progress}% · <span className="text-muted-soft">{euro(data.yearly_revenue)}</span></div>
            </div>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: "var(--sage-light)" }}>
            <div className="h-full rounded-full bg-sage transition-all"
              style={{ width: `${Math.min(100, data.tva_progress)}%` }} />
          </div>
        </div>
      </div>
    </div>
  );
}
